import { ArrowUpOnSquareIcon, DocumentDuplicateIcon, DocumentIcon, ExclamationCircleIcon, PlusIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useDropArea } from 'react-use';
import { Flexor, Spinner } from 'shared/src/components';
import { ScreenReaderOnly } from 'shared/src/components/Accessibility';
import DateFormatter from 'shared/src/components/DateFormatter';
import { LinkAnchor, LinkButton } from 'shared/src/components/Links';
import { Alert, AlertDescription, Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from 'shared/src/components/ui/new';
import { classNames } from 'shared/src/utils/classNames';
import { downloadDocument, getFileExtension } from 'shared/src/utils/downloadDocument';
import { toast } from 'sonner';
import { getDocumentFile, getListOfDocuments, uploadDocument } from '../fetchers';
import { useAppSelector } from '../hooks';
import PageEmptyState from './PageEmptyState';

const TEN_MEGABYTES = 10 * 1024 * 1024;

type Document = {
  DocumentId: string;
  DocumentName: string;
  DocumentUrl: string;
  UploadedBy: string;
  DateCreated: string;
};

export default function Documents() {
  const currentUser = useAppSelector((state) => state.currentPollworkerUser);
  const [showUploadDocument, setShowUploadDocument] = useState<boolean>(false);
  const [documentName, setDocumentName] = useState<string>('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bond, { over: isDroppingFile }] = useDropArea({
    onFiles: ([file]) => updateFileToUpload(file),
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  function loadDocuments() {
    setLoadingDocuments(true);

    getListOfDocuments()
      .then((response) => {
        setDocuments(response);
      })
      .finally(() => {
        setLoadingDocuments(false);
      });
  }

  function updateFileToUpload(file?: File) {
    if (!file) return;

    if (file.size > TEN_MEGABYTES) {
      setErrorMessage('The file is too large.');
      return;
    }

    setErrorMessage(undefined);
    setFileToUpload(file);
    setDocumentName(file.name);
  }

  function downloadDocumentFile(document: Document) {
    const { DocumentId: fileId, DocumentName: fileName, DocumentUrl: documentUrl } = document;

    const promise = getDocumentFile(fileId)
      .then(async (resp) => (resp.ok ? await resp.blob() : undefined))
      .then((blob) => {
        if (!blob) throw new Error('Could not download document');

        return blob;
      })
      .then(downloadDocument(fileName, getFileExtension(documentUrl)));

    toast.promise(promise, {
      loading: `Downloading ${fileName}...`,
      success: `Downloaded ${fileName}!`,
      error: `Could not download file ${fileName}.`,
    });
  }

  async function uploadDocumentFile() {
    if (!fileToUpload || !documentName) return;

    setUploadingFile(true);

    await uploadDocument(currentUser.EVUserId, currentUser.Customer.Id, documentName, fileToUpload)
      .then(([success]) => {
        if (!success) {
          setErrorMessage('Something went wrong. Please try again.');
          return;
        }

        closeUploadModal();
        toast.success('File has been uploaded!');
      })
      .finally(() => {
        setUploadingFile(false);
        loadDocuments();
      });
  }

  function removeFileToUpload() {
    if (fileInputRef.current) fileInputRef.current.value = '';

    setFileToUpload(undefined);
    setDocumentName('');
    setErrorMessage(undefined);
  }

  function closeUploadModal() {
    removeFileToUpload();
    setShowUploadDocument(false);
  }

  const uploadDisabled = uploadingFile || !fileToUpload || !documentName;

  return (
    <div className="mt-2">
      <Dialog
        size="md"
        open={showUploadDocument}
        onOpenChange={(open) => {
          if (!open) closeUploadModal();
        }}
      >
        <DialogContent className="p-0 gap-0 overflow-hidden">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => e.preventDefault()}>
            <DialogBody className="space-y-5">
              {errorMessage ? (
                <Alert variant="destructive">
                  <ExclamationCircleIcon className="size-5" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <div>
                <Label htmlFor="file-upload">File to upload</Label>
                <div {...bond} className={classNames(isDroppingFile ? 'animate-pulse border-primary' : 'border-gray-900/25', 'mt-2 rounded-lg border border-dashed')}>
                  <div className="flex justify-center px-6 py-10">
                    <div className="text-center">
                      <ArrowUpOnSquareIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                      <div className="mt-4 flex text-sm leading-6 text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md bg-white font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:text-primary"
                        >
                          <LinkAnchor>{fileToUpload ? 'Replace file' : 'Upload a file'}</LinkAnchor>
                          <input
                            ref={fileInputRef}
                            onChange={({ target: { files } }) => updateFileToUpload(files?.[0])}
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs leading-5 text-gray-600">PDF, XLSX, CSV, PNG, JPG, GIF up to 10MB</p>
                    </div>
                  </div>
                  {fileToUpload ? (
                    <div className="m-2 border border-gray-300 rounded-lg text-sm p-2 leading-5 text-gray-600 flex items-center space-x-2">
                      <Flexor className="space-x-1 shrink text-wrap">
                        <DocumentIcon className="h-5 w-5 shrink-0" />
                        <span>{fileToUpload.name}</span>
                      </Flexor>
                      <button type="button" data-testid="remove-file" aria-label="Remove file" onClick={removeFileToUpload}>
                        <XCircleIcon className="h-5 w-5 shrink-0" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              <div>
                <Label htmlFor="document-name">Name</Label>
                <div className="mt-2">
                  <Input id="document-name" name="document-name" type="text" value={documentName} onChange={({ target: { value } }) => setDocumentName(value)} />
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button size="sm" onClick={uploadDocumentFile} disabled={uploadDisabled}>
                <Flexor className="space-x-2">
                  <Spinner light show={uploadingFile} />
                  <span>Upload</span>
                </Flexor>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <div>
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">Documents</h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the documents in your account including their name, the date they were uploaded, and who they were uploaded by.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <Button size="sm" className="mx-auto" onClick={() => setShowUploadDocument(true)}>
              <PlusIcon aria-hidden="true" />
              Add document
            </Button>
          </div>
        </div>
        {loadingDocuments && !documents.length ? (
          <div className="w-full flex justify-center items-center h-24 my-24 space-x-2">
            <Spinner show />
            <span className="text-lg">Loading documents...</span>
          </div>
        ) : null}
        {!loadingDocuments && !documents.length ? (
          <div className="my-10">
            <PageEmptyState icon={DocumentDuplicateIcon} emptyMessageHeader="No documents" emptyMessageSubHeader="Get started by uploading a new document.">
              <div className="mt-5">
                <Button size="sm" variant="outline" type="button" className="mx-auto" onClick={() => setShowUploadDocument(true)}>
                  <PlusIcon aria-hidden="true" />
                  Add document
                </Button>
              </div>
            </PageEmptyState>
          </div>
        ) : null}
        {!loadingDocuments && documents.length ? (
          <div className="-mx-4 mt-8 sm:-mx-0">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                    Name
                  </th>
                  <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                    Date Created
                  </th>
                  <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                    Uploaded By
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <ScreenReaderOnly>Download</ScreenReaderOnly>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {documents.map((document) => (
                  <tr key={document.DocumentId}>
                    <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0 truncate">
                      <LinkAnchor onClick={() => downloadDocumentFile(document)}>{document.DocumentName}</LinkAnchor>
                      <dl className="font-normal lg:hidden">
                        <dt className="sr-only sm:hidden">Date Created</dt>
                        <dd className="mt-1 truncate text-gray-500 sm:hidden">
                          <DateFormatter dateString={document.DateCreated} withTime={false} />
                        </dd>
                        <dt className="sr-only">Uploaded By</dt>
                        <dd className="mt-1 truncate text-gray-700 sm:hidden">{document.UploadedBy}</dd>
                      </dl>
                    </td>
                    <td className="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                      <DateFormatter dateString={document.DateCreated} withTime={false} />
                    </td>
                    <td className="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">{document.UploadedBy}</td>
                    <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                      <LinkButton onClick={() => downloadDocumentFile(document)}>Download</LinkButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
